import type { JSX } from 'react';
import { Store, useStore } from 'dg-web-shared/lib/Flux.tsx';
import { Body } from '../../../ui/layout/TabContent.tsx';
import * as Text from '../../../common/i18n/Text.ts';
import { AbstractLegacyServerState } from 'dg-web-shared/lib/AbstractLegacyServerStateSlice.ts';
import * as TomState from '../../state/TomState.ts';
import * as TomCollectionsState from '../../state/TomCollectionsState.ts';
import * as CurrentOperatorLoginState from '../../../common/state/CurrentOperatorLoginState.ts';
import { collectionTexts } from '../../i18n/TomTexts.ts';
import { CollectionsFilterSummary } from './CollectionsFilterSummary.tsx';
import { CollectionsList } from './CollectionsList.tsx';
import { DateFilterHalfSlideIn } from './DateFilter.tsx';
import { FilterHalfSlideIn } from './FilterHalfSlideIn.tsx';
import { TomFilterCollectionsListSlideIn } from './TomFilterCollectionsListSlideIn.tsx';
import { CollectionDetailSlideIn } from './CollectionDetailSlideIn.tsx';
import { isDefined, isUndefined } from 'dg-web-shared/lib/MaybeV2.ts';
import { SyncAppSlideIn } from '../../../sync-app/components/SyncAppSlideIn.tsx';
import * as SyncAppHelpSlideInState from '../../../sync-app/state/SyncAppHelpSlideInState.ts';
import { openUsbAppOrInfo } from '../../../sync-app/actions/SyncAppActions.ts';
import {
    FilterLayout,
    FilterLayoutBody,
    FilterLayoutHeader,
} from '../../../ui/filter/FilterLayout.tsx';
import { RestrictedComponent } from '../../../app/components/RestrictedComponent.tsx';
import { CustomPermissionCheckers } from '../../../app/state/Permission.ts';
import {
    ButtonDropdown,
    ButtonDropdownItem,
    ButtonDropdownLinkItem,
    ButtonDropdownSeparator,
} from '../../../ui/slidein/ButtonDropdown.tsx';
import { OperatorAccount } from '../../../api/Http.ts';
import { stringifyParams } from 'dg-web-shared/lib/QueryParamsUtil.ts';
import { OperatorAppRoutes } from '../../../app/config/OperatorRoutingConfig.tsx';
import { UsbAppAction } from 'taxomex-shared/usbApp/UsbAppAction.ts';
import { urlWithOperatorExternalId } from '../../../utils/Utils.ts';
import { OperatorContext } from '../../../app/components/BaseLayoutAndData.tsx';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { useContext } from 'react';

export interface CollectionTexts {
    TabTitle: Text.Translation;
    StartCollection: Text.Translation;
    MenuTitle: Text.Translation;
    Export: Text.Translation;
    Pdf: Text.Translation;
}

export function CollectionsBody() {
    const { operatorExternalId } = useContext(OperatorContext);
    const { storeState, update } = useStore(store => ({
        collections: TomCollectionsState.List.get(store),
        filter: TomCollectionsState.Filter.get(store),
        toms: TomState.List.get(store),
        detail: TomCollectionsState.Detail.get(store),
        currentLogin: CurrentOperatorLoginState.get(store),
        syncSlideIn: SyncAppHelpSlideInState.Layout.get(store),
    }));
    const { language } = useLanguage();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function getDependingStateSlices(): AbstractLegacyServerState<any>[] {
        return [storeState.toms, storeState.collections];
    }

    function renderSlideIns(): JSX.Element[] {
        return [
            <FilterHalfSlideIn key="filter" />,
            <DateFilterHalfSlideIn key="date-filter" />,
            <TomFilterCollectionsListSlideIn key="tom-filter" />,
            <CollectionDetailSlideIn key="collection-detail" />,
            <SyncAppSlideIn key="sync-app" />,
        ];
    }

    function isBodyDisabled(): boolean {
        const filterState = storeState.filter;
        const filterOpen =
            filterState.filterSlideInVisible ||
            filterState.tomSelectionVisible ||
            filterState.validDateSelectionVisible;
        return (
            isDefined(storeState.detail.selectedCollection) ||
            filterOpen ||
            storeState.syncSlideIn.open
        );
    }

    function readCollectionData(): void {
        const operatorId =
            storeState.currentLogin.data &&
            storeState.currentLogin.data.mandantId;
        update(store =>
            openUsbAppOrInfo(store, {
                usbAppAction: UsbAppAction.COLLECTION,
                usbAppArgs: { operatorId: operatorId as number },
                stateResetActionId: 'refetch-after-usb-collection',
                stateResetAction: (store: Store) => {
                    TomCollectionsState.List.reset(store);
                    TomState.List.reset(store);
                },
            }),
        );
    }

    function isLoading(): boolean {
        return storeState.syncSlideIn.loading;
    }

    if (isUndefined(storeState.currentLogin.data)) {
        return null;
    }

    const txt = collectionTexts[language];
    const queryParams = stringifyParams(
        OperatorAccount.TomCollections.paramsToQueryParams({
            validFrom: storeState.filter.validFrom,
            validTo: storeState.filter.validTo,
            toms: storeState.filter.toms,
            sticks: storeState.filter.sticks,
            hideEmptyCollections: storeState.filter.hideEmptyCollections,
        }),
    );
    return (
        <Body
            disabled={isBodyDisabled()}
            dependingStateSlices={getDependingStateSlices()}
            slideIns={renderSlideIns()}
            loading={isLoading()}
        >
            <FilterLayout>
                <FilterLayoutHeader
                    filterSummary={<CollectionsFilterSummary />}
                    rightDropdown={
                        <ButtonDropdown label={txt.MenuTitle()}>
                            <RestrictedComponent
                                route={OperatorAppRoutes.ParkingMeters}
                                permissionChecker={
                                    CustomPermissionCheckers.syncAppInteractionAllowed
                                }
                            >
                                <div>
                                    <ButtonDropdownItem
                                        onClick={() => readCollectionData()}
                                        label={txt.StartCollection()}
                                    />
                                    <ButtonDropdownSeparator />
                                </div>
                            </RestrictedComponent>

                            <ButtonDropdownLinkItem
                                href={urlWithOperatorExternalId(
                                    `/ui-api/operator-account/taxomex/tom-collections/pdf?${queryParams}`,
                                    operatorExternalId,
                                )}
                                label={txt.Pdf()}
                                disabled={
                                    storeState.collections.data.size === 0
                                }
                            />
                            <ButtonDropdownLinkItem
                                href={urlWithOperatorExternalId(
                                    `/ui-api/operator-account/taxomex/tom-collections/csv?${queryParams}`,
                                    operatorExternalId,
                                )}
                                label={txt.Export()}
                                disabled={
                                    storeState.collections.data.size === 0
                                }
                            />
                        </ButtonDropdown>
                    }
                />

                <FilterLayoutBody>
                    <CollectionsList />
                </FilterLayoutBody>
            </FilterLayout>
        </Body>
    );
}
